import { Text, TextStyle, type Container, Graphics } from 'pixi.js';
import { ComboCounter } from './ComboCounter';
import { ChartRenderer } from './ChartRenderer'; // For convertChartSecondsToBeat
import {
  FLOAT_COMPARISON_EPSILON,
  DEBUG_HOLD_TICK_LINE_COLOR,
  DEBUG_HOLD_TICK_LINE_THICKNESS
} from './constants';
import type { ChartData, ParsedNote } from '~/types';

export interface NoteDebugInfoParams {
  note: ParsedNote;
  comboCounter?: ComboCounter;
  container: Container;
  noteDrawnBottomY: number;
  noteCenterX: number;
}

export interface HoldSegmentDebugInfoParams {
  noteUid: number;
  segmentInitialStartTimeSeconds: number;
  comboCounter?: ComboCounter;
  container: Container;
  segmentStartX: number;
  segmentStartY: number;
  segmentStartLanePos: number;
}

export interface HoldTickLineParams {
  container: Container;
  xAbsolute: number;
  yAbsolute: number;
  gridLineThickness: number;
  displayLaneL: number;
  displayLaneR: number;
}

export interface HoldSegmentTicksDebugParams {
  container: Container;
  noteWhoseHoldsDefineTicks: ParsedNote;
  allParsedNotesMapForTicks: Map<number, ParsedNote>;
  overallHoldSequenceInitialBeat: number;
  overallHoldSequenceFinalBeat: number;
  overallHoldSequenceInitialLaneL: number;
  overallHoldSequenceInitialLaneR: number;
  overallHoldSequenceFinalLaneL: number;
  overallHoldSequenceFinalLaneR: number;
  visualPieceStartBeat: number;
  visualPieceEndBeat: number;
  bpms: ChartData['Bpms'];
  chartOffset: number;
  getBeatXOffset: (beat: number) => number;
  getBeatYOffset: (beat: number) => number;
  gridLineThickness: number;
}

export interface GridTimeDebugInfoParams {
  container: Container;
  beatIndex: number;
  xPos: number;
  yPos: number;
  laneWidth: number;
  gridLineThickness: number;
  bpms: ChartData['Bpms'];
  chartOffset: number;
}

export class ChartRendererDebug {
  public static drawNoteDebugInfo(params: NoteDebugInfoParams): void {
    const { note, comboCounter, container, noteDrawnBottomY, noteCenterX } = params;
    let uidTextContent = String(note.uid);
    if (comboCounter) {
      const comboCount = comboCounter.getCumulativeComboAtBeatKey(
        ComboCounter.getBeatKey(note.just)
      );
      if (comboCount !== undefined) {
        uidTextContent += ` (C:${comboCount})`;
      }
    }
    uidTextContent += `\nB:${note.just}`;
    const uidStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 10,
      fill: 0xcccccc,
      align: 'center'
    });
    const uidText = new Text({ text: uidTextContent, style: uidStyle });
    uidText.anchor.set(0.5, 0);
    uidText.x = noteCenterX;
    uidText.y = noteDrawnBottomY + 2;
    container.addChild(uidText);
  }

  public static drawHoldSegmentDebugInfo(params: HoldSegmentDebugInfoParams): void {
    const {
      noteUid,
      segmentInitialStartTimeSeconds,
      comboCounter,
      container,
      segmentStartX,
      segmentStartY,
      segmentStartLanePos
    } = params;

    let debugTextContent = `${noteUid}`;
    if (comboCounter && segmentInitialStartTimeSeconds !== undefined) {
      const comboCount = comboCounter.getCumulativeComboAtBeatKey(
        ComboCounter.getBeatKey(segmentInitialStartTimeSeconds)
      );
      if (comboCount !== undefined) {
        debugTextContent += ` (C:${comboCount})`;
      }
    }
    debugTextContent += `\nB:${segmentInitialStartTimeSeconds}`;
    const uidStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 10,
      fill: 0xcccccc,
      align: 'right'
    });
    const uidText = new Text({ text: debugTextContent, style: uidStyle });
    uidText.anchor.set(1, 0.5);
    uidText.x = segmentStartX + segmentStartLanePos - 5;
    uidText.y = segmentStartY;
    container.addChild(uidText);
  }

  public static drawHoldTickLine(params: HoldTickLineParams): void {
    const { container, xAbsolute, yAbsolute, gridLineThickness, displayLaneL, displayLaneR } =
      params;
    const tickLine = new Graphics();
    const yCenter = yAbsolute + gridLineThickness / 2;

    tickLine
      .moveTo(xAbsolute + displayLaneL, yCenter)
      .lineTo(xAbsolute + displayLaneR, yCenter)
      .stroke({ width: DEBUG_HOLD_TICK_LINE_THICKNESS, color: DEBUG_HOLD_TICK_LINE_COLOR });

    container.addChild(tickLine);
  }

  public static drawHoldSegmentTicksDebug(params: HoldSegmentTicksDebugParams): void {
    const {
      container,
      noteWhoseHoldsDefineTicks,
      allParsedNotesMapForTicks,
      overallHoldSequenceInitialBeat,
      overallHoldSequenceFinalBeat,
      overallHoldSequenceInitialLaneL,
      overallHoldSequenceInitialLaneR,
      overallHoldSequenceFinalLaneL,
      overallHoldSequenceFinalLaneR,
      visualPieceStartBeat,
      visualPieceEndBeat,
      bpms,
      chartOffset,
      getBeatXOffset,
      getBeatYOffset,
      gridLineThickness
    } = params;

    for (const holdLink of noteWhoseHoldsDefineTicks.holds) {
      let tickTimeSeconds: number | undefined;
      if (holdLink.type === 'beat') {
        tickTimeSeconds = holdLink.beat;
      } else {
        const targetNote = allParsedNotesMapForTicks.get(holdLink.uid);
        if (targetNote) {
          tickTimeSeconds = targetNote.just; // This is in seconds
        }
      }

      if (tickTimeSeconds === undefined) continue;

      const tickActualBeat = ChartRenderer.convertChartSecondsToBeat(
        tickTimeSeconds,
        bpms,
        chartOffset
      );

      if (
        tickActualBeat >= visualPieceStartBeat - FLOAT_COMPARISON_EPSILON &&
        tickActualBeat <= visualPieceEndBeat + FLOAT_COMPARISON_EPSILON
      ) {
        const totalOverallHoldDurationBeats =
          overallHoldSequenceFinalBeat - overallHoldSequenceInitialBeat;
        let tickLaneL = overallHoldSequenceInitialLaneL;
        let tickLaneR = overallHoldSequenceInitialLaneR;

        if (Math.abs(totalOverallHoldDurationBeats) > FLOAT_COMPARISON_EPSILON) {
          const fractionInOverallHold = Math.max(
            0,
            Math.min(
              1,
              (tickActualBeat - overallHoldSequenceInitialBeat) / totalOverallHoldDurationBeats
            )
          );
          tickLaneL =
            overallHoldSequenceInitialLaneL +
            (overallHoldSequenceFinalLaneL - overallHoldSequenceInitialLaneL) *
              fractionInOverallHold;
          tickLaneR =
            overallHoldSequenceInitialLaneR +
            (overallHoldSequenceFinalLaneR - overallHoldSequenceInitialLaneR) *
              fractionInOverallHold;
        } else if (
          Math.abs(tickActualBeat - overallHoldSequenceFinalBeat) < FLOAT_COMPARISON_EPSILON
        ) {
          tickLaneL = overallHoldSequenceFinalLaneL;
          tickLaneR = overallHoldSequenceFinalLaneR;
        }

        this.drawHoldTickLine({
          container,
          xAbsolute: getBeatXOffset(tickActualBeat),
          yAbsolute: getBeatYOffset(tickActualBeat),
          gridLineThickness,
          displayLaneL: Math.min(tickLaneL, tickLaneR),
          displayLaneR: Math.max(tickLaneL, tickLaneR)
        });
      }
    }
  }

  public static drawGridTimeDebugInfo(params: GridTimeDebugInfoParams): void {
    const { container, beatIndex, xPos, yPos, laneWidth, gridLineThickness, bpms, chartOffset } =
      params;

    const timeInSeconds = ChartRenderer.convertBeatToChartSeconds(beatIndex, bpms, chartOffset);
    const timeTextStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 8,
      fill: 0x888888,
      align: 'center'
    });
    const timeText = new Text({ text: `${timeInSeconds.toFixed(5)}s`, style: timeTextStyle });
    timeText.anchor.set(0.5, 0);
    timeText.x = xPos + laneWidth / 2;
    timeText.y = yPos + gridLineThickness + 2; // Position below the grid line
    container.addChild(timeText);
  }
}
